import { useEffect, useMemo, useState } from "react";
import { describeText, asciiName, CSS_ESCAPE_NOTE, type EmojiInfo } from "./emojiCodes";
import { ToolContainer } from "@/components/ToolLayout";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogUsageOnAction } from "@/hooks/useLogUsageOnAction";
import { copyText } from "@/lib/copyText";

const TOOL_INFO_CONTENT = {
  what: `Converts emoji (including multi-part ones like families, flags and skin tones) into Unicode code points, HTML entities, JavaScript, CSS and Python escapes, and UTF-8 bytes, with the official emoji name — useful for developers who need to reference a specific emoji in code.`,
  how: `Type or paste an emoji, and its Unicode codepoint along with HTML and JavaScript representations are displayed, each ready to copy.`,
  tip: `Some emoji are actually made up of multiple combined Unicode codepoints (like a family emoji, which combines several individual person emoji) — this tool keeps each of those sequences (families, flags, skin tones, keycaps) together as one character and lists every code point inside it.`,
};

export default function EmojiUnicodeConverter() {
  const [input, setInput] = useState("😀🚀❤️");

  const analysis = useMemo(() => describeText(input), [input]);
  const [names, setNames] = useState<Map<string, string> | null>(null);
  useEffect(() => {
    let live = true;
    // Official names come from the same dataset as the Emoji Picker; loaded once, only on this page.
    import("emojibase-data/en/compact.json").then((m) => {
      if (!live) return;
      const map = new Map<string, string>();
      type E = { hexcode: string; label: string; skins?: E[] };
      for (const e of (m.default as E[])) { map.set(e.hexcode, e.label); for (const k of e.skins ?? []) map.set(k.hexcode, k.label); }
      setNames(map);
    }).catch(() => {});
    return () => { live = false; };
  }, []);
  const nameOf = (hexcode: string) => names?.get(hexcode) ?? names?.get(hexcode.replace(/-FE0F/g, "")) ?? names?.get(hexcode.replace(/-FE0F/g, "").concat("-FE0F")) ?? "";

  const { logUsageNow } = useLogUsageOnAction();

  const copy = async (v: string) => {
    const allowed = await logUsageNow("copy");
    if (!allowed) return;
    void copyText(v, "Copied to clipboard");
  };


  return (
    <ToolContainer>
      <Label htmlFor="eu-input" className="mb-2 block">Emoji or text</Label>
      <Textarea
        id="eu-input"
        value={input}
        onChange={e => setInput(e.target.value)}
        className="min-h-[80px] text-2xl mb-6"
      />

      <p className="text-xs text-muted-foreground -mt-4 mb-4">{CSS_ESCAPE_NOTE}</p>
      <div className="space-y-3">
        {analysis.map((item, i) => {
          const name = nameOf(item.hexcode) || asciiName(item.char);
          const rows: [string, keyof EmojiInfo][] = [["HTML (decimal)", "htmlDecimal"], ["HTML (hex)", "htmlHex"], ["JavaScript", "js"], ["JS (UTF-16 pairs)", "jsSurrogates"], ["CSS", "css"], ["Python", "python"], ["UTF-8 bytes", "utf8"]];
          return (
            <Card key={i} className="p-4">
              <div className="flex items-center gap-4 mb-3">
                <span className="text-4xl">{item.char}</span>
                <div className="min-w-0">
                  {name && <p className="text-sm font-medium capitalize">{name}</p>}
                  <p className="text-sm font-mono text-muted-foreground break-words">{item.unicode}</p>
                  {item.codepoints.length > 1 && <p className="text-xs text-muted-foreground">{item.codepoints.length} code points joined into one character</p>}
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs font-mono">
                {rows.map(([label, key]) => (
                  <button key={key} onClick={() => copy(String(item[key]))} className="text-left p-2 rounded bg-muted/50 hover:bg-muted" title={`Copy ${label}`}>
                    <span className="block text-[10px] uppercase tracking-wide text-muted-foreground font-sans">{label}</span>
                    <span className="break-all">{String(item[key])}</span>
                  </button>
                ))}
              </div>
            </Card>
          );
        })}
      </div>
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
